import { ReactElement } from "react";
import { createPortal } from "react-dom";
import { Draggable } from "@hello-pangea/dnd";
import { HugeiconsIcon } from "@hugeicons/react";
import {
  DragDropVerticalIcon,
  ViewIcon,
  ViewOffSlashIcon
} from "@hugeicons-pro/core-stroke-rounded";
import { IconButton, IconButtonVariant } from "shared/components/IconButton";
import { IconButtonSize } from "shared/components";
import { IconSize } from "shared/components/IconBridge";
import { cx } from "shared/utils";
import { Show } from "shared/components";
import { NavItemId } from "../constants";
import { formatNavBadgeCount } from "../use-nav-item-counts";
import { NavCountPill } from "./NavCountPill";
import { ResolvedNavItem } from "../nav-settings";
import { useNavCustomization } from "../use-nav-customization";
import { NavLinkContent } from "./NavLinkContent";

const getIconIndicator = (id: NavItemId, count: number) => {
  if (!count) return undefined;
  if (id === NavItemId.NOTIFICATIONS) return count;
  if (id === NavItemId.INBOX) return formatNavBadgeCount(count);
  return undefined;
};

export const CustomizeNavRow = ({
  item,
  index,
  count
}: {
  item: ResolvedNavItem;
  index: number;
  count: number;
}) => {
  const { toggleHidden } = useNavCustomization();
  const visibilityLabel = `${item.isHidden ? "Show" : "Hide"} ${item.name}`;

  return (
    <Draggable draggableId={item.id} index={index}>
      {(provided, snapshot) => {
        const row: ReactElement = (
          <div
            ref={provided.innerRef}
            {...provided.draggableProps}
            className={cx(
              "my-0.5 flex items-center rounded-lg bg-white",
              snapshot.isDragging ? "shadow-lg" : ""
            )}
          >
            <div
              {...provided.dragHandleProps}
              aria-label={`Reorder ${item.name}`}
              className="flex h-10 w-6 shrink-0 cursor-grab items-center justify-center rounded-lg text-content-200 hover:text-content-400 active:cursor-grabbing"
            >
              <HugeiconsIcon
                icon={DragDropVerticalIcon}
                size={16}
                strokeWidth={2}
              />
            </div>
            <div
              title={item.name}
              className={cx(
                "relative flex min-w-0 flex-1 items-center gap-4 overflow-hidden whitespace-nowrap p-3 text-sm font-medium text-content-500 stroke-content-500",
                item.isHidden ? "opacity-40" : ""
              )}
            >
              <NavLinkContent
                size={IconSize.BASE}
                HugeIcon={item.Icon}
                strokeWidth={item.strokeWidth}
                isBeta={item.isBeta}
                indicator={getIconIndicator(item.id, count)}
              >
                <span className="flex min-w-0 items-center gap-2">
                  <span className="truncate">{item.name}</span>
                  <Show if={item.id === NavItemId.RENT_REPORTING && count > 0}>
                    <NavCountPill count={count} />
                  </Show>
                </span>
              </NavLinkContent>
            </div>
            <IconButton
              variant={IconButtonVariant.TEXT_BASE}
              size={IconButtonSize.SM}
              HugeIcon={item.isHidden ? ViewOffSlashIcon : ViewIcon}
              aria-label={visibilityLabel}
              title={visibilityLabel}
              onClick={() => toggleHidden(item.id)}
            />
          </div>
        );

        if (snapshot.isDragging) return createPortal(row, document.body);
        return row;
      }}
    </Draggable>
  );
};
